/* eslint-disable no-console */
const isDevelopment: boolean = process.env['NODE_ENV'] !== 'production';

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Logger function with development/production branching
const log = (level: string, message: string, data?: unknown): void => {
  const timestamp = new Date().toISOString();

  if (isDevelopment) {
    // biome-ignore lint/suspicious/noConsole: Logger library intentionally uses console
    console.log(
      `[${timestamp}] ${level.toUpperCase()}: ${message}`,
      data ?? '',
    );
  } else {
    // Use structured JSON logging for production/Vercel
    const logData = {
      timestamp,
      level,
      message,
      ...(Boolean(data) && typeof data === 'object' ? data : {}),
    };

    // Use the appropriate console method for Vercel to capture logs properly
    if (level === 'error') {
      console.error(JSON.stringify(logData));
    } else if (level === 'warn') {
      console.warn(JSON.stringify(logData));
    } else {
      // biome-ignore lint/suspicious/noConsole: Logger library intentionally uses console
      console.info(JSON.stringify(logData));
    }
  }
};

export const info = (message: string, data?: unknown): void =>
  log('info', message, data);
export const warn = (message: string, data?: unknown): void =>
  log('warn', message, data);
export const error = (message: string, data?: unknown): void =>
  log('error', message, data);
export const debug = (message: string, data?: unknown): void => {
  if (isDevelopment) log('debug', message, data);
};

const logger: {
  info: typeof info;
  warn: typeof warn;
  error: typeof error;
  debug: typeof debug;
} = { info, warn, error, debug };
export default logger;

// Error tracking interface (provider-agnostic)
interface ErrorContext {
  readonly [key: string]: unknown;
}

/**
 * User information for error tracking context.
 * @privacy The email field contains PII - only set with explicit user consent.
 * Provider implementations should consider hashing or redacting email
 * before sending to external services.
 */
interface UserInfo {
  readonly id: string;
  readonly email?: string;
}

/**
 * Provider-agnostic error tracking interface.
 * Implementations can integrate with Datadog, Sentry, or other providers.
 */
export interface ErrorTracker {
  captureException(error: Readonly<Error>, context?: ErrorContext): void;
  captureMessage(message: string, level?: 'info' | 'warn' | 'error'): void;
  /**
   * Set user context for error tracking.
   * @privacy Only call with explicit user consent for PII fields like email.
   */
  setUser(user: UserInfo): void;
}

// Default tracker implementation (logs to console)
const defaultTracker: ErrorTracker = {
  captureException: (error_: Readonly<Error>, context?: ErrorContext): void => {
    error(error_.message, { ...context, stack: error_.stack });
  },
  captureMessage: (
    message: string,
    level: 'info' | 'warn' | 'error' = 'info',
  ): void => {
    log(level, message);
  },
  setUser: (_user: UserInfo): void => {
    // No-op in default implementation
  },
};

let currentTracker: ErrorTracker = defaultTracker;

export const errorTracker: ErrorTracker = {
  captureException: (error_: Readonly<Error>, context?: ErrorContext): void => {
    currentTracker.captureException(error_, context);
  },
  captureMessage: (
    message: string,
    level?: 'info' | 'warn' | 'error',
  ): void => {
    currentTracker.captureMessage(message, level);
  },
  setUser: (user: UserInfo): void => {
    currentTracker.setUser(user);
  },
};

let isTrackerInitialized = false;

/**
 * Sets a custom error tracker implementation.
 *
 * @important Must be called synchronously during app initialization,
 * before any async operations begin. The errorTracker facade reads
 * from a global reference, so calling this during async flows could
 * lead to race conditions where some errors use the old tracker.
 *
 * @param tracker - The error tracker implementation to use
 */
export function setErrorTracker(tracker: Readonly<ErrorTracker>): void {
  if (isTrackerInitialized) {
    warn('setErrorTracker called multiple times - replacing existing tracker');
  }
  currentTracker = tracker;
  isTrackerInitialized = true;
}

/**
 * Resets the error tracker to the default console-logging implementation.
 * Intended for use in tests to ensure clean state between test cases.
 *
 * @example
 * afterEach(() => {
 *   resetErrorTracker();
 * });
 */
export function resetErrorTracker(): void {
  currentTracker = defaultTracker;
  isTrackerInitialized = false;
}
